// Player stats from the two sources the tracker gets them from: the
// live-scores/player-stats API (teams[].players[].stats) or, without it, the
// feed's playerStatistics. Shared by the player stats tab and the half-time
// summary on the pitch.

// Stats read per player, in display order. `fields`: keys in the API's
// players[].stats; `ids` / `match`: the feed's playerStatistics[].statistics.
export const PLAYER_STAT_COLUMNS = [
    { key: 'shots', fields: ['totalShots'], ids: ['2255'], label: 'statistics.playershots' },
    { key: 'ontarget', fields: ['shotsOnTarget'], ids: ['2005'], label: 'statistics.playershotsontarget' },
    { key: 'passes', fields: ['completedPasses', 'passes'], ids: ['2151'], label: 'statistics.playerpasses' },
    { key: 'tackles', fields: ['tackles'], ids: ['2003'], label: 'statistics.playertackles' },
    { key: 'fouls', fields: ['fouls'], ids: ['2007'], label: 'statistics.playerfouls' },
    { key: 'interceptions', fields: ['interceptions'], ids: ['2115'], label: 'statistics.playerinterceptions' },
    { key: 'saves', fields: ['saves'], ids: ['2004'], label: 'statistics.playersaves' },
    { key: 'xg', fields: ['xg', 'xG', 'expectedGoals'], match: /(^|\W)xg(\W|$)|expected goals/i, label: 'statistics.playerxg', decimals: 2 }
];

// Goals, assists, cards and minutes, in the same two formats.
const EXTRAS = {
    goals: { field: 'goals', id: '1030' },
    assists: { field: 'assists', id: '1036' },
    yellow: { field: 'yellowCards', id: '2155' },
    red: { field: 'redCards', id: '2173' },
    minutes: { field: 'minutesPlayed', id: '102' }
};

const toNumber = v => {
    const n = parseFloat(String(v ?? '').replace(',', '.'));
    return isNaN(n) ? 0 : n;
};

// Feed stat -> column.
export function columnOfFeedStat(stat) {
    const id = String(stat.id ?? stat.Id ?? '');
    const name = String(stat.name ?? stat.Name ?? '');
    return PLAYER_STAT_COLUMNS.find(c => (c.ids && c.ids.includes(id)) || (c.match && c.match.test(name)));
}

// Team ids by side: event ids first, then the competitors' positions.
export function teamIdsOf(matchData) {
    const comps = matchData?.competitors || [];
    const byPos = pos => comps.find(c => String(c.position ?? c.Position) === pos)?.id;
    return {
        home: String(matchData?.home_id || byPos('1') || ''),
        away: String(matchData?.away_id || byPos('2') || '')
    };
}

// Both sources as { key, name, teamId, side, played, stats: {column: n}, extras }.
// A stat missing from `stats` is not available for that player.
export function readPlayers(playerStats, matchData) {
    const teams = playerStats?.teams;
    if (Array.isArray(teams) && teams.length) {
        return teams.flatMap(team => {
            const side = team.side === 'home' || String(team.position) === '1' ? 'home' : team.side === 'away' || String(team.position) === '2' ? 'away' : null;
            return (team.players || []).map((p, idx) => {
                const raw = p.stats || {};
                // Stats the feed did not send, which the API filled in with 0:
                // not real values, so left out like the ones not sent at all.
                const missing = new Set(Array.isArray(p.missingStats) ? p.missingStats : []);
                const stats = {};
                PLAYER_STAT_COLUMNS.forEach(c => {
                    const field = c.fields.find(k => raw[k] !== undefined && raw[k] !== null && !missing.has(k));
                    if (field) stats[c.key] = toNumber(raw[field]);
                });
                const extras = {};
                Object.entries(EXTRAS).forEach(([k, e]) => { extras[k] = toNumber(raw[e.field]); });
                return { key: p.playerId || `${team.id}-${idx}`, name: p.name || '', teamId: String(team.id || ''), side, played: p.hasPlayed === true, stats, extras };
            });
        });
    }
    const ls = matchData?.Livescore || matchData?.livescore;
    const list = matchData?.playerStatistics || ls?.playerStatistics || ls?.PlayerStatistics;
    if (!Array.isArray(list)) return [];
    const teamIds = teamIdsOf(matchData);
    return list.map((p, idx) => {
        const stats = {};
        const byId = {};
        (p.statistics || p.Statistics || []).forEach(st => {
            const value = toNumber(st.value ?? st.Value);
            byId[String(st.id ?? st.Id)] = value;
            const col = columnOfFeedStat(st);
            if (col) stats[col.key] = value;
        });
        const extras = {};
        Object.entries(EXTRAS).forEach(([k, e]) => { extras[k] = byId[e.id] || 0; });
        const teamId = String(p.teamId ?? p.TeamId ?? '');
        const side = teamId && teamId === teamIds.home ? 'home' : teamId && teamId === teamIds.away ? 'away' : null;
        const played = p.hasPlayed === true || Object.values(byId).some(v => v > 0);
        return { key: p.playerId || p.PlayerId || idx, name: p.playerName || p.PlayerName || p.name || '', teamId, side, played, stats, extras };
    });
}

// "Lautaro Martinez" -> "L Martinez".
export function shortPlayerName(name) {
    const parts = String(name).trim().split(/\s+/);
    return parts.length > 1 ? `${parts[0][0]} ${parts.slice(1).join(' ')}` : String(name).trim();
}
